import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import type { SpreadsheetDocument } from 'models/SpreadsheetDocument';
import type { SpreadsheetBatchExportResult } from './exportSpreadsheet';

interface SpreadsheetBatchDownloadProps {
  documents: SpreadsheetDocument[];
  disabled: boolean;
  exportDocuments: (documents: SpreadsheetDocument[]) => Promise<SpreadsheetBatchExportResult>;
}

const SpreadsheetBatchDownload: React.FC<SpreadsheetBatchDownloadProps> = ({
  documents,
  disabled,
  exportDocuments,
}) => {
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SpreadsheetBatchExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (busyRef.current || disabled || !documents.length) return;
    busyRef.current = true;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      setResult(await exportDocuments(documents));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'ZIP 다운로드에 실패했습니다.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const closeResult = () => {
    setResult(null);
    setError(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        disabled={disabled || !documents.length || busy}
        onClick={handleDownload}
        sx={{ minHeight: 44 }}
      >
        {busy ? 'ZIP 생성 중...' : '전체 다운로드 (ZIP)'}
      </Button>
      <Dialog
        open={!!result || !!error}
        onClose={closeResult}
        maxWidth="sm"
        fullWidth
        aria-labelledby="spreadsheet-download-result"
      >
        <DialogTitle id="spreadsheet-download-result">전체 다운로드 결과</DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <Alert severity={result?.fileCount ? 'success' : 'warning'}>
                {result?.fileCount
                  ? `${result.fileCount}개의 파일을 ZIP으로 묶어 다운로드했습니다.`
                  : '다운로드할 수 있는 문서가 없습니다.'}
              </Alert>
              {!!result?.issues.length && (
                <Box sx={{ mt: 2 }}>
                  <Typography fontWeight={700}>제외된 문서 {result.issues.length}개</Typography>
                  <Box component="ul" sx={{ pl: 2.5, overflowWrap: 'anywhere' }}>
                    {result.issues.map((issue, index) => (
                      <li key={index}>
                        <Typography fontWeight={600}>{issue.name || '(제목 없음)'}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {issue.message}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeResult} sx={{ minHeight: 44 }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SpreadsheetBatchDownload;
